import React, { useEffect, useRef, useState } from "react";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import LayoutIcon from "@/Components/LayoutIcon";
import { IconAi, Pie } from "@/Icons";
import AppLayout from "@/Layouts/AppLayout";
import { Dropdown, Formheader, Subheader, GrayBox } from "@/Components";
import { usePage } from "@inertiajs/inertia-react";
import { usePayload } from "@/Contexts/PayloadContext";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  LabeledSelect,
} from "@/Components/Form";
import { Inertia } from "@inertiajs/inertia";
import { toast } from "react-toastify";
import { convertWeightToHumanReadable } from "@/Utilities/Utilities";
import { MoreVertical, User } from "react-feather";
import { ActionMenu } from "@/Components/ActionMenu";

export default function InventorySetup() {
  const { payload, onFormChange } = usePayload();

  const teams = usePage().props.teams?.data || [];
  const questions = usePage().props.questions?.data || [];
  const years =
    usePage().props.years?.map((year) => ({ value: year, label: year })) || [];

  const ref = useRef(null);

  const isDisabled = () => ref.current?.checkValidity() !== true;

  const generate = (e) => {
    e.preventDefault();
    Inertia.visit(route("emissions.inventory.build", payload));
  };

  return (
    <>
      <GrayBox margin="mb-4">
        <h2 className="mb-4 text-xl font-bold">Generate inventory</h2>

        <form ref={ref} className="flex flex-wrap gap-4">
          <LabeledSelect
            required
            title="Year of inventory"
            name="inventory_year"
            options={years}
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            value={payload?.inventory_year}
          />
          <LabeledSelect
            required
            title="Base year for comparison"
            name="base_year"
            options={years}
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            value={payload?.base_year}
          />
          <RadioGroup identifier="scope_3_emissions" />
          <RadioGroup identifier="biogenic_combustion_emissions" />
          <RadioGroup identifier="pcc_report_format" />

          <LabeledInput
            required
            title="Name of inventory"
            name="inventory_name"
            onChange={onFormChange}
            containerClass="w-full"
            placeholder="Enter a name for the inventory"
            value={payload?.inventory_name || ""}
          />

          <LabeledMultiSelectAutocomplete
            required
            title="Teams"
            name="teams"
            options={teams.map((team) => ({
              value: team.id,
              label: team.name,
            }))}
            placeholder="Teams"
            initialChecked={true}
            defaultValue={payload?.teams || []}
            onSelect={(val) =>
              onFormChange({
                target: {
                  name: "teams",
                  value: val.map((team) => team.value),
                },
              })
            }
            className="w-full sm:basis-1/2-gap-4"
          />

          <LabeledMultiSelectAutocomplete
            required
            title="Activities"
            name="questions"
            options={questions.map((question) => ({
              value: question.id,
              label: question.title,
            }))}
            placeholder="Activities"
            initialChecked={true}
            defaultValue={payload?.questions || []}
            onSelect={(val) =>
              onFormChange({
                target: {
                  name: "questions",
                  value: val.map((question) => question.value),
                },
              })
            }
            className="w-full sm:basis-1/2-gap-4"
          />

          <div className="mt-4 flex w-full justify-end">
            <button className="btn" disabled={isDisabled()} onClick={generate}>
              Generate inventory
            </button>
          </div>
        </form>
      </GrayBox>
      <GeneratedReports />
    </>
  );
}

InventorySetup.layout = (page) => (
  <AppLayout title="Emission inventory">
    <OrganizationLayout
      header="Emission inventory"
      icon={
        <LayoutIcon>
          <Pie size={50} className="text-black" />
        </LayoutIcon>
      }
    >
      {page}
    </OrganizationLayout>
  </AppLayout>
);

function RadioGroup({ identifier, className = "w-full sm:basis-1/3-gap-4" }) {
  const { payload, onFormChange } = usePayload();

  const variations = {
    scope_3_emissions: {
      label: "Scope 3 emissions included",
      options: [
        { key: "yes", label: "Yes" },
        { key: "no", label: "No" },
      ],
      defaultOption: "yes",
    },
    biogenic_combustion_emissions: {
      label: "Is Biogenic combustion emissions included",
      options: [
        { key: "yes", label: "Yes" },
        { key: "no", label: "No" },
      ],
      defaultOption: "yes",
    },
    pcc_report_format: {
      label: "IPCC report format",
      options: [
        { key: "ar5", label: "AR5" },
        { key: "ar4", label: "AR4" },
      ],
      defaultOption: "ar5",
    },
  };

  useEffect(() => {
    if (!payload[identifier]) {
      onFormChange({
        target: {
          name: identifier,
          value: variations[identifier].defaultOption,
        },
      });
    }
  }, []);

  return (
    <div className={className}>
      <Formheader>{variations[identifier].label}</Formheader>
      {variations[identifier].options.map((option) => (
        <div key={`${identifier}_${option.key}`}>
          <input
            required
            type="radio"
            className="mr-2 p-2"
            id={`${identifier}_${option.key}_radio`}
            name={identifier}
            value={option.key}
            onChange={onFormChange}
            checked={option.key === payload[identifier]}
          />
          <label htmlFor={`${identifier}_${option.key}_radio`} className="ml-2">
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );
}

function GeneratedReports() {
  const reports = usePage().props?.reports || [];

  const [sort, setSort] = useState("date.desc");

  const sortOptions = [
    { value: "date.desc", label: "Newest first" },
    { value: "date.asc", label: "Oldest first" },
    { value: "name.asc", label: "Name A-Ö" },
    { value: "name.desc", label: "Name Ö-A" },
  ];

  if (reports.length === 0) {
    return null;
  }

  const sortedReports = reports.sort((a, b) => {
    const [sortKey, sortDirection] = sort.split(".");
    if (sortKey === "date") {
      return sortDirection === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortKey === "name") {
      return sortDirection === "asc"
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    }
    return 0;
  });

  return (
    <>
      <div className="mb-2 flex items-end justify-between gap-4">
        <Subheader margin="" className="mt-12">
          Generated inventories
        </Subheader>
        <Dropdown
          options={sortOptions}
          title="Sort on"
          placeholder={
            sortOptions.find((o) => o.value === sort)?.label || "Newest first"
          }
          onSelect={(value) => {
            setSort(value);
          }}
        />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
        {sortedReports.map((report) => (
          <ReportComponent key={report.id} report={report} />
        ))}
      </div>
    </>
  );
}

export const ReportComponent = ({ report }) => {
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = report.filepath;
    link.setAttribute("download", `${report.title}.docx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDelete = () => {
    const id = toast.loading("Please wait...");
    Inertia.delete(route("emissions.inventory.delete", report.id), {
      onSuccess: () => {
        toast.update(id, {
          render: "The report has successfully been deleted",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: "Sorry, we could not delete this report",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  };

  let actionMenuItems = [
    {
      label: "Download report",
      callback: () => handleDownload(),
    },
    {
      label: "Delete report",
      callback: () => handleDelete(),
    },
    {
      label: (
        <span className="flex gap-2">
          <IconAi /> Generate insights
        </span>
      ),
      callback: () =>
        Inertia.visit(route("emissions.inventory.insights", { report })),
    },
  ];

  const actionMenuRef = useRef(null);

  return (
    <div className="flex flex-col items-start gap-8 bg-gray-light px-8 py-6 shadow-small">
      <div className="flex min-h-[100px] items-start self-stretch">
        <div className="flex grow flex-col items-start gap-1">
          <div className="self-stretch hyphens-auto break-all font-overpass text-2xl font-bold">
            {report.title}
          </div>

          <div className="rounded-md bg-blue-lighter px-3 py-2 text-sm leading-none">
            {convertWeightToHumanReadable(report?.emission || 0)?.concat} CO₂e
          </div>
        </div>
        <div className="hover-circle relative z-10 ml-2">
          <MoreVertical
            size={24}
            className="shrink-0 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              actionMenuRef.current.toggleMenu();
            }}
          />
          <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
        </div>
      </div>
      <div className="flex flex-wrap items-end justify-end gap-2 self-stretch">
        <div className="flex shrink-0 items-start gap-4">
          <div className="flex items-center gap-2">
            <User size={24} />
            <div>{report.team_count} teams</div>
          </div>
        </div>
      </div>
    </div>
  );
};
